/* ===================================================================
   Requests - the SINGLE home for asking customers for a review and
   tracking who responded. (Rebuilt by Hopper, 2026-07-28.)

   WHAT THIS SCREEN DOES:
   - WYSIWYG: the SUBJECT + BODY the operator composes are EXACTLY what
     sends (server-side merge-tag substitution + review-link + CAN-SPAM
     footer). No hidden hardcoded copy.
   - TEMPLATES-FIRST: the composer DEFAULTS to a strong neutral template
     (subject + body pre-filled). The owner can edit inline or pick "Write
     your own." Zero freestyling required.
   - EMAIL is the only SENDABLE channel. SMS is SHOWN but clearly LOCKED
     (it unlocks on a paid plan, when the dedicated business number is
     provisioned). SMS never sends from here.
   - HONEST send status from the real per-row send result (Sent vs
     queued-with-reason), never a blanket "nothing sent".
   - CARD-AT-SEND lives HERE: a card-free owner's first real send shows the
     inline card step on THIS page, then fires the send. One request screen,
     including the first-time card moment (the old RequestReviewsModal +
     onboarding popup are fully retired).
   - Live PREVIEW stacked UNDERNEATH the editor, labelled "Preview".

   HARD PRODUCT RULES honored (do not regress):
   - Nothing auto-sends: the send fires ONLY on an explicit operator click.
   - FTC-neutral ask (Hard Rule #4): default copy has no star-rating steering
     and no gating; the review link is always included (backend injects it if
     custom copy omits it).
   - Quiet hours system-enforced in the customer's local tz; do-not-contact
     customers are always skipped.
   =================================================================== */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon, ClosePath } from '../components/ui';
import type { ChipKind } from '../components/ui';
import { InlineCardStep } from '../components/InlineCardStep';
import { SmsTimelineExplainer } from '../components/SmsTimelineExplainer';
import {
  useRequests,
  useRequestsSummary,
  useCreateRequests,
  useCustomers,
  useTemplates,
  useSessionToken,
  useRequireCardToSend,
  useBusiness,
} from '../hooks';
import { ApiError, type RequestRow, type RequestsCreated } from '../lib/api';
import { avatarColor, fullName, initialOf, relativeTime } from '../lib/display';
import '../styles/components.css';
import '../styles/requests.css';

const REVIEW_LINK = '{{review_link}}';

/* Built-in default review-request templates (email). Neutral + FTC-compliant:
   no star-rating steering, no gating (Hard Rule #4). The composer DEFAULTS to
   #0. Capped at 4 generic-but-strong defaults for this pass.
   TODO(Les): final wording polish. TODO(follow-on): business-TYPE-specific sets
   (junk hauler vs vet) are a SEPARATE build. */
interface ComposerTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
}
const DEFAULT_TEMPLATES: ComposerTemplate[] = [
  {
    id: 'builtin:0',
    name: 'Friendly thank-you',
    subject: 'A quick favor from {{business}}',
    body: `Hi {{first_name}},\n\nThanks so much for choosing {{business}}! If you have a moment, would you share your experience in a quick Google review? It helps other neighbors find us and means a lot.\n\nLeave a review here: ${REVIEW_LINK}\n\nThank you!`,
  },
  {
    id: 'builtin:1',
    name: 'Short & sweet',
    subject: 'Quick review for {{business}}?',
    body: `Hi {{first_name}}, thanks again for trusting {{business}}. A quick Google review would really help other neighbors find us: ${REVIEW_LINK}\n\nThank you!`,
  },
  {
    id: 'builtin:2',
    name: 'Personal note',
    subject: 'Thank you from {{business}}',
    body: `Hi {{first_name}},\n\nIt was a pleasure working with you. Honest feedback from customers like you helps a small business like {{business}} grow. If you're willing, would you leave us a Google review?\n\n${REVIEW_LINK}\n\nThanks so much.`,
  },
  {
    id: 'builtin:3',
    name: 'How did we do?',
    subject: 'How did we do, {{first_name}}?',
    body: `Hi {{first_name}},\n\nThanks for choosing {{business}}. We'd love to hear how everything went. Would you take 20 seconds to leave a Google review?\n\n${REVIEW_LINK}\n\nWe appreciate it!`,
  },
];

const MERGE_TAGS = ['{{first_name}}', '{{business}}', '{{review_link}}'];

/* Sentinel customer-picker value for "Select all eligible customers". Chosen as
   a value no real customer id can collide with. */
const ALL_ELIGIBLE = '__all_eligible__';

type ResultTone = 'ok' | 'warn' | 'err';
interface SendResult {
  tone: ResultTone;
  text: string;
}

function statusDisplay(status: string | null): { label: string; kind: ChipKind } {
  switch ((status || '').toLowerCase()) {
    case 'left_review':
      return { label: 'Left a review', kind: 'green' };
    case 'opened':
      return { label: 'Opened', kind: 'blue' };
    case 'delivered':
      return { label: 'Delivered', kind: 'gray' };
    case 'reminder_sent':
      return { label: 'Reminder sent', kind: 'gold' };
    case 'sent':
      return { label: 'Sent', kind: 'green' };
    case 'failed':
      return { label: 'Failed', kind: 'red' };
    case 'stopped':
      return { label: 'Stopped', kind: 'red' };
    case 'queued':
    default:
      return { label: 'Queued', kind: 'gold' };
  }
}

function pct(part: number, whole: number): number {
  if (!whole) return 0;
  return Math.round((part / whole) * 100);
}

/* Turn a backend queued reason into honest, plain owner-facing copy. Nothing
   here ever claims a send happened when it did not. */
function queuedCopy(reason: string, name: string): string {
  switch (reason) {
    case 'no_customer_email':
      return `${name} has no email on file. Add an email address to send.`;
    case 'email_disabled':
      return `Saved as queued for ${name}. Email sending is not switched on yet, so nothing was sent.`;
    case 'no_review_link':
      return `Saved as queued for ${name}. Connect your Google listing so your review link resolves, then send again. Nothing was sent yet.`;
    case 'email_unavailable':
      return `Saved as queued for ${name}. Email is not configured on the server yet, so nothing was sent.`;
    case 'no_customer_phone':
      return `${name} has no mobile number on file. Add one to send a text, or send by email.`;
    case 'tenant_sms_not_authorized':
      return `Saved as queued for ${name}. Texting is not switched on for your account yet, so nothing was sent. Send by email instead.`;
    case 'no_consumer_consent':
      return `${name} has not confirmed they want texts yet (they reply YES to opt in). Nothing was sent. Send by email instead.`;
    case 'consumer_opted_out':
      return `${name} replied STOP to texts, so no text was sent.`;
    case 'outside_send_window':
    case 'outside_local_window':
      return `Saved as queued for ${name}. Texts only go out during daytime hours, so nothing was sent right now. Try again later.`;
    default:
      return `Could not send to ${name} just yet${reason ? ` (${reason})` : ''}. The request is saved. Nothing was sent.`;
  }
}

/* ---- inline icons (stroke) ---- */
const CaretDown = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);
const SendIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16}>
    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" strokeLinejoin="round" />
  </svg>
);
/* Shown on the GREEN post-send state of the primary button. */
const CheckIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} width={16} height={16}>
    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const SearchIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4-4" />
  </svg>
);
const LockIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <rect x="4" y="11" width="16" height="9" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);
/* Funnel-tile chips. One glyph per stage so the three tiles are tellable apart
   on a phone at arm's length even before the label is read (Steve 2026-07-29). */
const TileSentIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" strokeLinejoin="round" />
  </svg>
);
const TileReachedIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
    <path d="M4 5h16v14H4z" />
    <path d="M4 6l8 6 8-6" strokeLinejoin="round" />
  </svg>
);
const TileReviewedIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M12 2l3 6.6 7 .6-5.3 4.6 1.6 6.9L12 17l-6.3 3.7 1.6-6.9L2 9.2l7-.6z" />
  </svg>
);

export function Requests() {
  useSessionToken();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Channel: email is the only SENDABLE channel; sms is shown-but-locked.
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [customerId, setCustomerId] = useState('');
  const [templateId, setTemplateId] = useState(DEFAULT_TEMPLATES[0].id);
  const [subject, setSubject] = useState(DEFAULT_TEMPLATES[0].subject);
  const [message, setMessage] = useState(DEFAULT_TEMPLATES[0].body);
  const [sendResult, setSendResult] = useState<SendResult | null>(null);
  const [funnelFocus, setFunnelFocus] = useState<'all' | 'reached' | 'reviewed'>('all');
  const [viewing, setViewing] = useState<RequestRow | null>(null);
  // Card-at-send: when true, the inline card step is shown OVER this screen and
  // the composed send fires on success (the first-real-send card moment lives
  // here now, not in a separate popup).
  const [showCard, setShowCard] = useState(false);

  const { data: summary } = useRequestsSummary({ days: 30 });
  const funnel = summary?.funnel ?? { sent: 0, delivered_or_opened: 0, left_review: 0 };

  const { data: reqData, isLoading: reqLoading } = useRequests({ limit: 50 });
  const recent: RequestRow[] = useMemo(() => reqData?.requests ?? [], [reqData]);

  // The funnel tiles are FILTERS: clicking one narrows the Recent list below to
  // that stage, so a tile actually goes somewhere instead of just drawing an
  // outline (Steve 2026-07-30). 'all' shows everything; 'reached' = delivered or
  // opened; 'reviewed' = left a review.
  const filteredRecent = useMemo(() => {
    if (funnelFocus === 'reached')
      return recent.filter((r) => ['delivered', 'opened'].includes((r.status || '').toLowerCase()));
    if (funnelFocus === 'reviewed')
      return recent.filter((r) => (r.status || '').toLowerCase() === 'left_review');
    return recent;
  }, [recent, funnelFocus]);
  const funnelLabel =
    funnelFocus === 'reached'
      ? 'Delivered or opened'
      : funnelFocus === 'reviewed'
        ? 'Left a review'
        : 'All requests';

  const { data: custData } = useCustomers({ limit: 500 });
  const customers = useMemo(
    () => (custData?.customers ?? []).filter((c) => !c.do_not_contact),
    [custData],
  );
  // Email-only send: only saved email templates are offered (in addition to the
  // built-in defaults above).
  const { data: tplData } = useTemplates();
  const savedTemplates = useMemo(
    () => (tplData?.templates ?? []).filter((t) => t.channel !== 'sms'),
    [tplData],
  );

  // The client's review-request photo, shown at the TOP of the preview email
  // exactly where it rides on the real send. Read from the business profile
  // (request_photo_url); absent -> the preview simply shows no photo.
  const business = useBusiness();
  const requestPhoto = business.data?.profile?.request_photo_url ?? null;

  const { createRequests, isPending, error } = useCreateRequests();
  // The ONE card-at-send guard (setup stays card-free; the card is required at
  // the first real send). inlineAvailable -> show the embedded card step here.
  const { inlineAvailable, interceptSend } = useRequireCardToSend();

  // Deep-link support: the topbar "Request reviews" button + the Customers
  // profile "Send review request" navigate here with ?customer=<id> to
  // preselect. Consume the param once, then strip it from the URL.
  useEffect(() => {
    const pre = searchParams.get('customer');
    if (pre) {
      setCustomerId(pre);
      setSendResult(null);
      searchParams.delete('customer');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === customerId),
    [customers, customerId],
  );
  const selectedNoEmail = !!selectedCustomer && !selectedCustomer.email;

  // "Select all eligible" bulk enroll. Eligible = a customer with an email on
  // file (the list is already filtered to exclude do-not-contact). Customers
  // with no email can't be emailed, so they're surfaced honestly, not sent.
  const isAllSelected = customerId === ALL_ELIGIBLE;
  const eligibleCustomers = useMemo(() => customers.filter((c) => c.email), [customers]);
  const noEmailCount = customers.length - eligibleCustomers.length;
  // The server caps ONE call at 200 ids AND at the 25/day tenant allowance, so a
  // giant list never fires as one blast. We slice to 200 here so the call never
  // hard-errors; the daily cap then decides how many actually go now.
  const bulkRecipientCount = Math.min(eligibleCustomers.length, 200);

  function pickTemplate(value: string) {
    setTemplateId(value);
    setSendResult(null);
    if (value === 'custom') {
      // "Write your own": blank the fields so the owner truly composes from
      // scratch. The backend still injects the review link + CAN-SPAM footer.
      setSubject('');
      setMessage('');
      return;
    }
    const builtin = DEFAULT_TEMPLATES.find((t) => t.id === value);
    if (builtin) {
      setSubject(builtin.subject);
      setMessage(builtin.body);
      return;
    }
    const saved = savedTemplates.find((t) => `saved:${t.id}` === value);
    if (saved) {
      setMessage(saved.body);
      if (saved.subject) setSubject(saved.subject);
    }
  }

  function insertTag(tag: string) {
    setMessage((m) => (m.endsWith(' ') || m.length === 0 ? m + tag : m + ' ' + tag));
  }

  // The actual send + honest status. Extracted so it fires either from the
  // primary button (subscribed/whitelisted owner) OR right after the inline
  // card step creates the trial subscription (card-free owner, no re-tap).
  // Honest one-line summary for a BULK "select all" send: how many went now,
  // how many were held by the 25/day cap, how many had no email. Nothing here
  // claims a send that did not happen.
  function bulkSummary(res: RequestsCreated, attempted: number): SendResult {
    const sent = res.sent_count;
    const held = res.skipped.filter((s) => s.reason === 'daily_cap_reached').length;
    const parts: string[] = [];
    if (sent > 0) parts.push(`Sent ${sent} now`);
    if (held > 0) {
      const cap = res.daily_cap ? ` (daily limit ${res.daily_cap})` : '';
      parts.push(`${held} held for tomorrow${cap}`);
    }
    if (noEmailCount > 0) parts.push(`${noEmailCount} skipped (no email on file)`);
    const extra = eligibleCustomers.length - attempted;
    if (extra > 0) parts.push(`${extra} not attempted this batch`);
    if (sent === 0 && held === 0) {
      // Nothing transmitted (e.g. email not switched on, or no review link).
      const reason = res.created[0]?.send?.reason || res.skipped[0]?.reason || '';
      return { tone: 'warn', text: queuedCopy(reason, 'your customers') };
    }
    const tone: ResultTone = held > 0 || noEmailCount > 0 ? 'warn' : 'ok';
    return { tone, text: parts.join('. ') + '.' };
  }

  async function fireSend() {
    // BULK: "Select all eligible" -> enroll everyone with an email, capped.
    if (isAllSelected) {
      if (eligibleCustomers.length === 0) return;
      setSendResult(null);
      const ids = eligibleCustomers.slice(0, 200).map((c) => c.id);
      try {
        const res = await createRequests({
          customer_ids: ids,
          channel: 'email',
          subject,
          message,
        });
        setSendResult(bulkSummary(res, ids.length));
        if (res.sent_count > 0) setCustomerId('');
      } catch (e) {
        if (e instanceof ApiError && (e.status === 402 || e.body?.error === 'subscription_required')) {
          if (inlineAvailable) setShowCard(true);
          else navigate('/choose-plan?intent=send');
          return;
        }
      }
      return;
    }
    if (!customerId || selectedNoEmail) return;
    setSendResult(null);
    const name = selectedCustomer ? fullName(selectedCustomer) : 'your customer';
    try {
      const res = await createRequests({
        customer_ids: [customerId],
        channel: 'email',
        subject,
        message,
      });
      // HONEST status: read the real per-row send result, not created_count.
      if (res.created_count === 0 && res.skipped_count > 0) {
        setSendResult({
          tone: 'err',
          text: `${name} was skipped (do-not-contact or already removed).`,
        });
        return;
      }
      const row = res.created[0];
      if (row?.send?.transmitted || row?.status === 'sent') {
        setSendResult({ tone: 'ok', text: `Sent to ${name}. The email is on its way.` });
        setCustomerId('');
      } else {
        setSendResult({ tone: 'warn', text: queuedCopy(row?.send?.reason || '', name) });
      }
    } catch (e) {
      // Backend card-at-send backstop (402) -- can happen in the brief window
      // after subscribing before the plan cache propagates. Re-open the inline
      // card step if available, else route to the plan screen.
      if (e instanceof ApiError && (e.status === 402 || e.body?.error === 'subscription_required')) {
        if (inlineAvailable) setShowCard(true);
        else navigate('/choose-plan?intent=send');
        return;
      }
      /* other errors surface via `error` below */
    }
  }

  async function handleSend() {
    if (isAllSelected ? eligibleCustomers.length === 0 : !customerId || selectedNoEmail) return;
    // Card-free owner with the inline step available -> show the embedded card
    // step ON THIS SCREEN (the last step of the send). It fires on success.
    if (inlineAvailable) {
      setShowCard(true);
      return;
    }
    // Card-free owner without the inline step -> hosted /choose-plan redirect.
    // Whitelisted/subscribed owners fall through and send normally.
    if (interceptSend()) return;
    await fireSend();
  }

  const reachedPct = pct(funnel.delivered_or_opened, funnel.sent);
  const reviewedPct = pct(funnel.left_review, funnel.sent);

  // Card-at-send inline step: shown OVER the screen; on success auto-fires the
  // composed send the owner already built (no re-tap).
  if (showCard) {
    return (
      <InlineCardStep
        recipientCount={isAllSelected ? bulkRecipientCount : 1}
        onClose={() => setShowCard(false)}
        onSubscribed={() => {
          setShowCard(false);
          void fireSend();
        }}
      />
    );
  }

  return (
    <div className="req-wrap">
      {/* PAGE HEADER */}
      <div className="req-head">
        <div>
          <h1 className="req-head__title">Requests</h1>
          <p className="req-head__sub">
            Ask for a review by email, then see exactly who responded.
          </p>
        </div>
        <div className="req-search">
          {SearchIcon}
          <input type="search" placeholder="Search customers..." aria-label="Search customers" />
        </div>
      </div>

      {/* FUNNEL STRIP: sent (BLUE) -> reached (GREEN) -> reviewed (GOLD).
          Colour-coded per Steve 2026-07-29 so the three stages are tellable
          apart instantly on a phone. Colour is a redundant cue only -- the
          label and the glyph still carry the meaning for colour-blind users. */}
      <div className="req-funnel">
        <div
          className={'req-tile req-tile--sent' + (funnelFocus === 'all' ? ' is-active' : '')}
          onClick={() => setFunnelFocus('all')}
        >
          <span className="req-tile__chip" aria-hidden="true">{TileSentIcon}</span>
          <div className="req-tile__lab">REQUESTS SENT</div>
          <div className="req-tile__num">{funnel.sent}</div>
          <div className="req-tile__sub">This month · 100%</div>
        </div>
        <div
          className={'req-tile req-tile--reached' + (funnelFocus === 'reached' ? ' is-active' : '')}
          onClick={() => setFunnelFocus('reached')}
        >
          <span className="req-tile__chip" aria-hidden="true">{TileReachedIcon}</span>
          <div className="req-tile__lab">DELIVERED OR OPENED</div>
          <div className="req-tile__num">
            {funnel.delivered_or_opened}{' '}
            <span className="req-tile__pct">· {reachedPct}%</span>
          </div>
          <div className="req-tile__sub">Reached the customer</div>
        </div>
        <div
          className={'req-tile req-tile--reviewed' + (funnelFocus === 'reviewed' ? ' is-active' : '')}
          onClick={() => setFunnelFocus('reviewed')}
        >
          <span className="req-tile__chip" aria-hidden="true">{TileReviewedIcon}</span>
          <div className="req-tile__lab">LEFT A REVIEW</div>
          <div className="req-tile__num">
            {funnel.left_review}{' '}
            <span className="req-tile__pct">· {reviewedPct}%</span>
          </div>
          <div className="req-tile__sub">Converted to a Google review</div>
        </div>
      </div>

      {/* COMPOSER (editor) then PREVIEW stacked underneath */}
      <div className="req-compose">
        {/* editor */}
        <div className="req-card req-card--editor">
          <div className="req-card__head">
            <div className="req-seclabel">New request</div>
            {/* Channel segment: Email is sendable; SMS is shown but LOCKED. */}
            <div className="req-seg" role="tablist" aria-label="Channel">
              <button
                className={channel === 'email' ? 'is-active' : ''}
                onClick={() => setChannel('email')}
                role="tab"
                aria-selected={channel === 'email'}
              >
                Email
              </button>
              <button
                className={'req-seg__locked' + (channel === 'sms' ? ' is-active' : '')}
                onClick={() => setChannel('sms')}
                role="tab"
                aria-selected={channel === 'sms'}
                title="SMS unlocks on a paid plan"
              >
                <span className="req-seg__lockico">{LockIcon}</span>
                SMS
              </button>
            </div>
          </div>

          {channel === 'sms' ? (
            /* SMS locked panel -- honest: the feature is real, gated behind a
               paid plan (we provision the dedicated number when you go paid). */
            <div className="req-locked">
              <div className="req-locked__ico">{LockIcon}</div>
              <div className="req-locked__title">Texting unlocks on a paid plan</div>
              <p className="req-locked__body">
                SMS unlocks when you upgrade to a paid plan. We set up your dedicated business
                texting number when you go paid, so your review requests come from your business.
                On your free trial, email is the way to reach customers.
              </p>
              <div className="req-locked__actions">
                <button className="req-send" onClick={() => navigate('/choose-plan?intent=sms')}>
                  See paid plans
                </button>
                <button className="req-view" onClick={() => setChannel('email')}>
                  Use email for now
                </button>
              </div>
              {/* The owner is standing on the SMS tab asking "when does this
                  work?" -- answer it here, open, honestly. Same component as
                  the card step so the story never drifts between screens. */}
              <div style={{ width: '100%', maxWidth: 560, marginTop: 6, textAlign: 'left' }}>
                <SmsTimelineExplainer defaultOpen />
              </div>
            </div>
          ) : (
            <>
              <div className="req-fields">
                <div>
                  <span className="req-lab">CUSTOMER</span>
                  <div className="req-select">
                    <select
                      value={customerId}
                      onChange={(e) => {
                        setCustomerId(e.target.value);
                        setSendResult(null);
                      }}
                      aria-label="Customer"
                    >
                      <option value="">Select a customer...</option>
                      {eligibleCustomers.length > 0 && (
                        <option value={ALL_ELIGIBLE}>
                          Select all eligible customers ({eligibleCustomers.length})
                        </option>
                      )}
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>
                          {fullName(c)}
                          {c.email ? ` - ${c.email}` : ' - (no email)'}
                        </option>
                      ))}
                    </select>
                    <span className="req-select__caret">{CaretDown}</span>
                  </div>
                </div>
                <div>
                  <span className="req-lab">TEMPLATE</span>
                  <div className="req-select">
                    <select
                      value={templateId}
                      onChange={(e) => pickTemplate(e.target.value)}
                      aria-label="Template"
                    >
                      {DEFAULT_TEMPLATES.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                      {savedTemplates.map((t) => (
                        <option key={t.id} value={`saved:${t.id}`}>
                          {t.name}
                        </option>
                      ))}
                      <option value="custom">Write your own</option>
                    </select>
                    <span className="req-select__caret">{CaretDown}</span>
                  </div>
                </div>
              </div>

              {/* Editable SUBJECT -- what you type here is the real email subject. */}
              <span className="req-lab">SUBJECT</span>
              <input
                className="req-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                aria-label="Subject"
                placeholder={DEFAULT_TEMPLATES[0].subject}
              />

              <span className="req-lab">MESSAGE</span>
              <textarea
                className="req-msg"
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                aria-label="Message"
              />

              <div className="req-tags">
                {MERGE_TAGS.map((tag) => (
                  <button key={tag} className="req-tag" type="button" onClick={() => insertTag(tag)}>
                    {tag}
                  </button>
                ))}
              </div>

              <p className="req-wysiwyg">
                What you write above is exactly what gets sent. Your Google review link is always
                included.
              </p>

              {isAllSelected && (
                <p className="req-wysiwyg">
                  {eligibleCustomers.length} eligible customer
                  {eligibleCustomers.length === 1 ? '' : 's'} will be enrolled in a 3-touch sequence
                  (day 0, 3, and 7), stopping automatically the moment they leave a review. To
                  protect your sending reputation, up to your daily limit go out now and the rest are
                  held for the next day.
                  {noEmailCount > 0 &&
                    ` ${noEmailCount} customer${noEmailCount === 1 ? '' : 's'} without an email on file will be skipped.`}
                </p>
              )}

              {/* Send posts ONLY on this explicit click. TRAFFIC-LIGHT states
                  (Steve 2026-07-30): GOLD when armed (customer + message ready),
                  GREEN "Sent" after a successful send, quiet otherwise. The green
                  state derives from the last send result (tone 'ok'), which is
                  cleared when the owner picks a new customer/template. */}
              {(() => {
                const sentOk = sendResult?.tone === 'ok';
                const canSend = isAllSelected
                  ? eligibleCustomers.length > 0
                  : !!customerId && !selectedNoEmail;
                const armed = canSend && !!message.trim();
                const sendClass =
                  'req-send' +
                  (sentOk ? ' req-send--sent' : armed ? ' req-send--armed' : '');
                const idleLabel = isAllSelected
                  ? `Send to ${eligibleCustomers.length} customer${eligibleCustomers.length === 1 ? '' : 's'}`
                  : 'Send request';
                return (
                  <button
                    className={sendClass}
                    onClick={handleSend}
                    disabled={!canSend || isPending || !message.trim()}
                  >
                    {sentOk ? CheckIcon : SendIcon}
                    {sentOk ? 'Sent' : isPending ? 'Sending...' : idleLabel}
                  </button>
                );
              })()}

              {selectedNoEmail && (
                <p className="req-result req-result--err" role="alert">
                  {selectedCustomer ? fullName(selectedCustomer) : 'This customer'} has no email on
                  file. Add an email address on their profile to send.
                </p>
              )}

              {/* HARD RULE: quiet hours system-enforced in the RECIPIENT's local tz.
                  COPY MUST MATCH THE ENGINE. engine/provisioning/area_code.py
                  (recipient_local_quiet_hours_ok) allows 8am-9pm recipient-local,
                  i.e. nothing goes out between 9pm and 8am. The UI said "8pm-8am"
                  until 2026-07-29; corrected here to match the code exactly. Do NOT
                  drift this string without changing the engine window. */}
              <div className="req-quiet">
                {LockIcon}
                Quiet hours are enforced automatically - a request that would land between 9pm and
                8am in the customer's local time is held until morning their time. Do-not-contact
                customers are always skipped.
              </div>

              {sendResult && (
                <p className={'req-result req-result--' + sendResult.tone} role="status">
                  {sendResult.text}
                </p>
              )}
              {error && (
                <p className="req-result req-result--err">
                  Could not send the request. Please try again.
                </p>
              )}
            </>
          )}
        </div>

        {/* live preview -- a MIRROR of the editor, stacked underneath */}
        <div className="req-card req-card--preview">
          <div className="req-preview__title">
            <span className="req-seclabel">Preview</span>
            <span className="req-preview__hint">This is what your customer receives</span>
          </div>
          {channel === 'sms' ? (
            <div className="req-locked req-locked--mini">
              <div className="req-locked__ico">{LockIcon}</div>
              <p className="req-locked__body">
                Text-message preview unlocks with your dedicated business number on a paid plan.
              </p>
            </div>
          ) : (
            <>
              <div className="req-email">
                <div className="req-email__head">
                  <div className="req-email__sublab">Subject</div>
                  <div className="req-email__subj">{subject || DEFAULT_TEMPLATES[0].subject}</div>
                </div>
                {requestPhoto && (
                  <div className="req-email__photo">
                    <img src={requestPhoto} alt="Your photo at the top of the review request" />
                  </div>
                )}
                <div className="req-email__body">{message}</div>
              </div>
              <div className="req-preview__note">
                Merge tags like {'{{first_name}}'} and {'{{business}}'} fill in per customer when you
                send.
              </div>
            </>
          )}
        </div>
      </div>

      {/* RECENT REQUESTS -- desktop table */}
      <div className="req-tbl req-desktop-only">
        <div className="req-tbl__head">
          <div className="req-seclabel">Recent requests</div>
          <div className="req-tbl__sub">
            {funnelFocus === 'all'
              ? 'Last 30 days'
              : `Showing: ${funnelLabel} (${filteredRecent.length})`}
            {funnelFocus !== 'all' && (
              <button className="req-tbl__clear" onClick={() => setFunnelFocus('all')}>
                Show all
              </button>
            )}
          </div>
        </div>
        <div className="req-tbl__cols">
          <div>CUSTOMER</div>
          <div>STAGE</div>
          <div>EMAIL</div>
          <div>LAST TOUCH</div>
          <div>STATUS</div>
          <div />
        </div>
        {reqLoading && <div className="req-empty">Loading requests...</div>}
        {!reqLoading && filteredRecent.length === 0 && (
          <div className="req-empty">
            {funnelFocus === 'all'
              ? 'No requests yet. Send your first request above.'
              : `No requests in "${funnelLabel}" yet. Tap "Requests sent" to see all.`}
          </div>
        )}
        {!reqLoading &&
          filteredRecent.map((r) => {
            const name = r.customer_name || 'Customer';
            const st = statusDisplay(r.status);
            const lastTouch = relativeTime(r.last_touch_at || r.created_at) || '-';
            return (
              <div className="req-tbl__row" key={r.id}>
                <div className="req-cust">
                  <span className="req-cust__ava" style={{ background: avatarColor(name) }}>
                    {initialOf(name)}
                  </span>
                  <span className="req-cust__name">{name}</span>
                </div>
                <div className="req-cell--muted">{r.stage || '-'}</div>
                <div className="req-cell">{r.email_status}</div>
                <div className="req-cell--muted">{lastTouch}</div>
                <div>
                  <span className={'req-badge req-badge--' + st.kind}>
                    <span className="req-badge__dot" />
                    {st.label}
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <button className="req-view" onClick={() => setViewing(r)}>
                    View
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {/* RECENT REQUESTS -- mobile card list */}
      <div className="req-mobile-only">
        <div className="req-mtitle req-seclabel">Recent requests</div>
        {funnelFocus !== 'all' && (
          <div className="req-mfilter">
            Showing: {funnelLabel} ({filteredRecent.length})
            <button className="req-tbl__clear" onClick={() => setFunnelFocus('all')}>
              Show all
            </button>
          </div>
        )}
        {reqLoading && <div className="req-empty">Loading requests...</div>}
        {!reqLoading && filteredRecent.length === 0 && (
          <div className="req-empty">
            {funnelFocus === 'all'
              ? 'No requests yet. Send your first request above.'
              : `No requests in "${funnelLabel}" yet.`}
          </div>
        )}
        {!reqLoading &&
          filteredRecent.map((r) => {
            const name = r.customer_name || 'Customer';
            const st = statusDisplay(r.status);
            const lastTouch = relativeTime(r.last_touch_at || r.created_at) || '-';
            return (
              <div
                className="req-mcard"
                key={r.id}
                onClick={() => setViewing(r)}
                role="button"
                tabIndex={0}
              >
                <span className="req-mcard__ava" style={{ background: avatarColor(name) }}>
                  {initialOf(name)}
                </span>
                <div className="req-mcard__mid">
                  <div className="req-mcard__name">{name}</div>
                  <div className="req-mcard__sub">
                    {(r.stage || '-')} · {lastTouch}
                  </div>
                </div>
                <span className={'req-badge req-badge--' + st.kind}>
                  <span className="req-badge__dot" />
                  {st.label}
                </span>
              </div>
            );
          })}
      </div>

      {viewing && (
        <div
          className="modal-bg"
          onClick={(e) => {
            if (e.target === e.currentTarget) setViewing(null);
          }}
        >
          <div className="modal">
            <div className="modal__head">
              <h3>Request detail</h3>
              <button className="x" onClick={() => setViewing(null)} aria-label="Close">
                <Icon d={ClosePath} />
              </button>
            </div>
            <div className="modal__body">
              <div className="pstats" style={{ marginBottom: 4 }}>
                <div className="pstat">
                  <b style={{ fontSize: 15 }}>{statusDisplay(viewing.status).label}</b>
                  <span>Status</span>
                </div>
                <div className="pstat">
                  <b style={{ fontSize: 15 }}>Email</b>
                  <span>Channel</span>
                </div>
                <div className="pstat">
                  <b style={{ fontSize: 15 }}>{viewing.stage || '-'}</b>
                  <span>Stage</span>
                </div>
              </div>
              <div className="sectlabel">Details</div>
              <div style={{ fontSize: 13.5, lineHeight: 1.9 }}>
                <div>
                  <span className="muted">Customer:</span> {viewing.customer_name || 'Customer'}
                </div>
                <div>
                  <span className="muted">Email status:</span> {viewing.email_status}
                </div>
                <div>
                  <span className="muted">Last touch:</span>{' '}
                  {viewing.last_touch_at ? relativeTime(viewing.last_touch_at) : '-'}
                </div>
                <div>
                  <span className="muted">Sent:</span>{' '}
                  {viewing.sent_at ? relativeTime(viewing.sent_at) : 'Not sent yet'}
                </div>
                <div>
                  <span className="muted">Request ID:</span>{' '}
                  <span style={{ fontFamily: 'var(--font-mono)' }}>{viewing.id}</span>
                </div>
              </div>
              <p className="muted" style={{ fontSize: 12.5, marginTop: 14, lineHeight: 1.5 }}>
                A "Sent" email has left for the customer. A "Queued" request is recorded but has not
                transmitted yet.
              </p>
            </div>
            <div className="modal__foot">
              {/* Secondary on purpose: the black-on-white pill is reserved for
                  the ONE primary send action so it always out-ranks everything
                  else on the screen (Steve 2026-07-29). */}
              <button className="req-view" onClick={() => setViewing(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
